import { IWorkflowFormTemplateState } from '@/store/workflow-form-template';
import { IWorkflowFormTemplateRevisionGetters } from './getters.types';
import {
  IWorkflowFormTemplateRevision,
  WorkflowFormTemplateRevision,
  IWorkflowFormTemplateRevisionData,
  WorkflowFormField,
  IWorkflowFormField,
  IWorkflowFormFieldData,
  WorkflowFormFieldOptionTodo,
  IWorkflowFormFieldOptionTodo,
  IWorkflowFormFieldOptionTodoData,
  WorkflowFormFieldOptionTodoTemplate,
  IWorkflowFormFieldOptionTodoTemplate,
  IWorkflowFormFieldOptionTodoTemplateData,
  WorkflowFormFieldOption,
  IWorkflowFormFieldOption,
  IWorkflowFormSectionData,
  WorkflowFormTemplateStateEnum,
  WorkflowFormTemplatePrefixTypeEnum,
  WorkflowFormTemplate,
  IWorkflowFormTemplateData,
  IWorkflowFormTemplate,
  WorkflowState,
  IWorkflowState,
  IWorkflowStateData,
  User,
  IUser,
  IUserData
} from '@/models';
import { IState, State } from '@/models/state/state';

export const getters: IWorkflowFormTemplateRevisionGetters = {
  getWorkflowFormTemplatesWithPublishedRevisions(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormTemplate[] {
    return state.workflowFormTemplatesWithPublishedRevisions.map(
      (data: IWorkflowFormTemplateData) => new WorkflowFormTemplate(data)
    );
  },

  getWorkflowFormTemplatesWithPublishedRevisionById: (
    state: IWorkflowFormTemplateState
  ) => (id: number): IWorkflowFormTemplate | undefined => {
    const data = state.workflowFormTemplatesWithPublishedRevisions.find(
      (workflowFormTemplate: IWorkflowFormTemplateData) =>
        workflowFormTemplate.id === id
    );
    if (!data) {
      return undefined;
    }

    return new WorkflowFormTemplate(data);
  },

  getWorkflowFormTemplates(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormTemplate[] {
    return state.workflowFormTemplates.map(
      (data: IWorkflowFormTemplateData) => new WorkflowFormTemplate(data)
    );
  },

  getWorkflowFormTemplateById: (state: IWorkflowFormTemplateState) => (
    id: number
  ): IWorkflowFormTemplate | undefined => {
    const data = state.workflowFormTemplates.find(
      (workflowFormTemplate: IWorkflowFormTemplateData) =>
        workflowFormTemplate.id === id
    );
    if (!data) {
      return undefined;
    }

    return new WorkflowFormTemplate(data);
  },

  getLastCreatedWorkflowFormTemplateId(
    state: IWorkflowFormTemplateState
  ): number | null {
    return state.lastCreatedWorkflowFormTemplateId;
  },

  getWorkflowFormTemplateRevisions(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormTemplateRevision[] {
    return state.workflowFormTemplateRevisions.map(
      (data: IWorkflowFormTemplateRevisionData) =>
        new WorkflowFormTemplateRevision(data)
    );
  },

  getWorkflowFormTemplateRevisionById: (state: IWorkflowFormTemplateState) => (
    id: number
  ): IWorkflowFormTemplateRevision | undefined => {
    const data = state.workflowFormTemplateRevisions.find(
      (workflowFormTemplateRevision: IWorkflowFormTemplateRevisionData) =>
        workflowFormTemplateRevision.id === id
    );
    if (!data) {
      return undefined;
    }

    return new WorkflowFormTemplateRevision(data);
  },

  getPublishedWorkflowFormTemplateRevisions(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormTemplateRevision[] {
    return state.workflowFormTemplateRevisions
      .filter(
        template => template.state == WorkflowFormTemplateStateEnum.Published
      )
      .map(
        (data: IWorkflowFormTemplateRevisionData) =>
          new WorkflowFormTemplateRevision(data)
      );
  },

  getLastCreatedWorkflowFormTemplateRevisionId(
    state: IWorkflowFormTemplateState
  ): number | null {
    return state.lastCreatedWorkflowFormTemplateRevisionId;
  },

  //fields
  getWorkflowFormFields(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormField[] {
    return state.formFields.map(
      (data: IWorkflowFormFieldData) => new WorkflowFormField(data)
    );
  },

  getWorkflowFormFieldById: (state: IWorkflowFormTemplateState) => (
    id: number
  ): IWorkflowFormField | undefined => {
    const data = state.formFields.find(
      (formFields: IWorkflowFormFieldData) => formFields.id === id
    );

    if (!data) {
      return undefined;
    }

    return new WorkflowFormField(data);
  },

  getWorkflowFormFieldsOptionsByTemplateId: (
    state: IWorkflowFormTemplateState,
    getters: any
  ) => (id: number): IWorkflowFormFieldOption[] => {
    const fieldsOptions = [] as IWorkflowFormFieldOption[];

    (getters.getWorkflowFormTemplateRevisionById(id)
      .sections! as IWorkflowFormSectionData[]).forEach(section => {
      section.section_fields.forEach(section_field => {
        const fieldOptions = (section_field.field_template as IWorkflowFormFieldData).options!.map(
          option => new WorkflowFormFieldOption(option)
        );

        if (fieldOptions.length > 0) {
          fieldsOptions.push(...fieldOptions);
        }
      });
    });

    return fieldsOptions;
  },

  getWorkflowFormFieldsOptionsByTemplateIdAndFieldId: (
    state: IWorkflowFormTemplateState,
    getters: any
  ) => (templateId: number, fieldId: number): IWorkflowFormFieldOption[] => {
    return getters
      .getWorkflowFormFieldsOptionsByTemplateId(templateId)
      .filter(
        (formOption: IWorkflowFormFieldOption) => formOption.field === fieldId
      );
  },

  getWorkflowFormFieldsOptionByTemplateIdAndOptionId: (
    state: IWorkflowFormTemplateState,
    getters: any
  ) => (
    templateId: number,
    optionId: number
  ): IWorkflowFormFieldOption | undefined => {
    const data = getters
      .getWorkflowFormFieldsOptionsByTemplateId(templateId)
      .find(
        (formOption: IWorkflowFormFieldOption) => formOption.id === optionId
      );

    if (!data) {
      return undefined;
    }

    return new WorkflowFormFieldOption(data);
  },

  //todos
  getWorkflowFormFieldOptionTodos(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormFieldOptionTodo[] {
    return state.formFieldOptionTodos.map(
      (data: IWorkflowFormFieldOptionTodoData) =>
        new WorkflowFormFieldOptionTodo(data)
    );
  },

  getWorkflowFormFieldOptionTodoById: (state: IWorkflowFormTemplateState) => (
    id: number
  ): IWorkflowFormFieldOptionTodo | undefined => {
    const data = state.formFieldOptionTodos.find(
      (formFields: IWorkflowFormFieldOptionTodoData) => formFields.id === id
    );

    if (!data) {
      return undefined;
    }

    return new WorkflowFormFieldOptionTodo(data);
  },

  getOptionTodosBySectionFieldAndOptionId: (
    state: IWorkflowFormTemplateState
  ) => (sectionId: number, fieldId: number): IWorkflowFormFieldOptionTodo[] => {
    const filteredTodos = state.formFieldOptionTodos.filter(
      (formFields: IWorkflowFormFieldOptionTodoData) =>
        formFields.section_field === sectionId &&
        formFields.field_option == fieldId
    );

    return filteredTodos.map(
      (data: IWorkflowFormFieldOptionTodoData) =>
        new WorkflowFormFieldOptionTodo(data)
    );
  },

  //todo templates
  getWorkflowFormTodoTemplates(
    state: IWorkflowFormTemplateState
  ): IWorkflowFormFieldOptionTodoTemplate[] {
    return state.formTodoTempates.map(
      (data: IWorkflowFormFieldOptionTodoTemplateData) =>
        new WorkflowFormFieldOptionTodoTemplate(data)
    );
  },

  getWorkflowFormTodoTemplateById: (state: IWorkflowFormTemplateState) => (
    id: number
  ): IWorkflowFormFieldOptionTodoTemplate | undefined => {
    const data = state.formTodoTempates.find(
      (
        workflowFormTemplateRevision: IWorkflowFormFieldOptionTodoTemplateData
      ) => workflowFormTemplateRevision.id === id
    );
    if (!data) {
      return undefined;
    }

    return new WorkflowFormFieldOptionTodoTemplate(data);
  },

  getWorkflowFormTemplatePrefixTypeVisuals: (
    state: IWorkflowFormTemplateState
  ): IState<WorkflowFormTemplatePrefixTypeEnum>[] => {
    return state.formTemplatePrefixTypes.map(visual => new State(visual));
  },

  getTemplateWorkflowStates: (
    state: IWorkflowFormTemplateState
  ): IWorkflowState[] => {
    return state.templateWorkflowStates.map(
      (data: IWorkflowStateData) => new WorkflowState(data)
    );
  },

  getWorkflowTemplateUsers: (
    state: IWorkflowFormTemplateState
  ): IUser[] => {
    return state.workflowTemplateUsers.map(
      (data: IUserData) => new User(data)
    );
  }
};
